import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { useAppDispatch, AppDispatch } from '../../redux/store'
import { addDelai } from '../../redux/delais/delaisActions'
import { Action } from '../../types/actionTypes'

interface DelaiCreateProps {
  onClose: () => void
  ActionId: string
  actionData?: Action
  onSuccess?: () => void
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}))

const DateCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const DisabledDateCard = styled(DateCard)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  opacity: 0.7,
}))

const EditableDateCard = styled(DateCard)(({ theme }) => ({
  borderColor: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.light + '10',
}))

const DateLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const DateValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}))

const DelaiCreate: React.FC<DelaiCreateProps> = ({
  ActionId,
  actionData,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()

  // Initialize with action's DateFinSupp if it exists
  const [additionalTime, setAdditionalTime] = useState<Date | null>(() => {
    if (actionData?.DateFinSupp) {
      return typeof actionData.DateFinSupp === 'string'
        ? new Date(actionData.DateFinSupp)
        : actionData.DateFinSupp
    }
    // If no DateFinSupp, suggest DateFin + 7 days
    if (actionData?.DateFin) {
      const endDateObj = new Date(actionData.DateFin)
      const suggested = new Date(endDateObj)
      suggested.setDate(suggested.getDate() + 7)
      return suggested
    }
    return null
  })

  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const startDate = actionData?.DateDebut
  const endDate = actionData?.DateFin

  const handleSubmit = async () => {
    if (!additionalTime) {
      setError(t('popups.additionalTimeModal.validation.required'))
      return
    }

    if (!startDate || !endDate) {
      setError(t('popups.additionalTimeModal.validation.submitError'))
      return
    }

    // TypeScript type narrowing - after this point, endDate is guaranteed to exist
    const validEndDate = endDate

    // Validate that additionalTime is after endDate
    const endDateObj = new Date(validEndDate)
    if (additionalTime <= endDateObj) {
      setError(t('popups.additionalTimeModal.validation.mustBeAfterEndDate'))
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const newDelai = {
        Date: additionalTime,
        ActionId: ActionId,
      }

      await dispatch(addDelai(newDelai)).unwrap()

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('popups.additionalTimeModal.validation.submitError')
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.target.value
    if (dateString) {
      const date = new Date(dateString + 'T00:00:00')
      setAdditionalTime(date)
      setError('')
    }
  }

  // Format dates for input type="date" (YYYY-MM-DD)
  const formatDateForInput = (date: Date | string | undefined | null) => {
    if (!date) return ''

    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (!dateObj || isNaN(dateObj.getTime())) {
      return ''
    }

    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Set minimum selectable date to endDate + 1 day
  const endDateObj = endDate ? new Date(endDate) : new Date()
  const minSelectableDate = new Date(endDateObj)
  minSelectableDate.setDate(minSelectableDate.getDate() + 1)

  const minDate = formatDateForInput(minSelectableDate)
  const currentValue = additionalTime ? formatDateForInput(additionalTime) : ''

  // Update additionalTime when modal opens
  useEffect(() => {
    if (actionData?.DateFinSupp) {
      const currentTime =
        typeof actionData.DateFinSupp === 'string'
          ? new Date(actionData.DateFinSupp)
          : actionData.DateFinSupp
      setAdditionalTime(currentTime)
    } else if (actionData?.DateFin) {
      // Suggest End Date + 7 days
      const endDateObj = new Date(actionData.DateFin)
      const suggested = new Date(endDateObj)
      suggested.setDate(suggested.getDate() + 7)
      setAdditionalTime(suggested)
    }
  }, [actionData])

  return (
    <StyledPaper>
      <Typography
        variant="h5"
        component="h2"
        sx={{ mb: 3, fontWeight: 600, color: '#132743' }}
      >
        {t('popups.additionalTimeModal.title')}
      </Typography>

      <Typography
        variant="body2"
        sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}
      >
        {t('popups.additionalTimeModal.description')}
      </Typography>

      <Grid container spacing={3}>
        {/* Start Date - Read Only */}
        <Grid item xs={12} md={4}>
          <DisabledDateCard>
            <DateLabel>
              <EventIcon fontSize="small" />
              {t('popups.additionalTimeModal.startDate')}
            </DateLabel>
            <DateValue>
              {startDate ? new Date(startDate).toLocaleDateString() : '-'}
            </DateValue>
            <Typography variant="caption" color="text.secondary">
              {t('popups.additionalTimeModal.readOnly')}
            </Typography>
          </DisabledDateCard>
        </Grid>

        {/* End Date - Read Only */}
        <Grid item xs={12} md={4}>
          <DisabledDateCard>
            <DateLabel>
              <EventIcon fontSize="small" />
              {t('popups.additionalTimeModal.endDate')}
            </DateLabel>
            <DateValue>
              {endDate ? new Date(endDate).toLocaleDateString() : '-'}
            </DateValue>
            <Typography variant="caption" color="text.secondary">
              {t('popups.additionalTimeModal.readOnly')}
            </Typography>
          </DisabledDateCard>
        </Grid>

        {/* Additional Time - Editable */}
        <Grid item xs={12} md={4}>
          <EditableDateCard>
            <DateLabel sx={{ color: 'primary.main' }}>
              <AccessTimeIcon fontSize="small" />
              {t('popups.additionalTimeModal.additionalTime')}
            </DateLabel>
            <TextField
              type="date"
              value={currentValue}
              onChange={handleDateChange}
              inputProps={{
                min: minDate,
              }}
              fullWidth
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="primary.main">
              {t('popups.additionalTimeModal.editable')}
            </Typography>
          </EditableDateCard>
        </Grid>
      </Grid>

      {/* Visual Flow Indicator */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: 'text.secondary',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'action.disabled',
            }}
          />
          <Box sx={{ flex: 1, height: 2, bgcolor: 'action.disabled' }} />
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'action.disabled',
            }}
          />
          <Box sx={{ flex: 1, height: 2, bgcolor: 'primary.main' }} />
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            px: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('popups.additionalTimeModal.start')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('popups.additionalTimeModal.original')}
          </Typography>
          <Typography variant="caption" color="primary.main">
            {t('popups.additionalTimeModal.extended')}
          </Typography>
        </Box>
      </Box>

      {/* Validation Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('popups.additionalTimeModal.validation.info')}
      </Alert>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 100 }}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !additionalTime}
          sx={{ minWidth: 150 }}
        >
          {isSubmitting
            ? t('popups.additionalTimeModal.submitting')
            : t('popups.additionalTimeModal.saveAdditionalTime')}
        </Button>
      </Box>
    </StyledPaper>
  )
}

export default DelaiCreate
